import { loadSupabase } from './client'

/**
 * **로그인 토큰을 받는 곳은 여기 하나다** (26-10-23).
 *
 * 제보 (10/23, 07:27 로그인 → 2시간 뒤): 위 계정 단추는 그대로인데 포인트 화면이 "로그인하세요 · 0P",
 * 유료 단추는 "포인트를 쓰지 못했어요" 를 띄웠다. 새로고침하면 다시 됐다.
 *
 * 까닭: 화면 곳곳이 `getSession()` 을 그대로 불렀다. supabase-js 는 토큰이 끝났으면 그 자리에서 새로 받는데,
 * 오래 잠들어 있던 탭에서 그 한 번이 실패하면(끊김·잠깐의 오류) **세션 없음(null)** 을 돌려준다.
 * 그러면 그 요청은 토큰 없이 나가 서버가 "로그인 안 한 사람" 으로 보고, 화면의 로그인 표시는 그대로 남는다.
 *
 * 그래서 여기서는
 *  - 끝나기 60초 전부터는 미리 새로 받고,
 *  - 새로 받기가 실패하면 잠깐 쉬었다가 두 번 더 해 보고,
 *  - 서버가 401 을 주면(토큰이 이미 끝났다) 한 번 새로 받아 **그 요청을 한 번 다시** 보낸다 (`authFetch`).
 * 여러 곳이 동시에 불러도 새로 받기는 한 번만 한다 (같은 약속을 나눠 쓴다).
 */
const EARLY_MS = 60_000
const RETRY_DELAYS = [400, 1200]

let refreshing: Promise<string | null> | null = null

async function refreshOnce(): Promise<string | null> {
  const client = await loadSupabase()
  for (let i = 0; i <= RETRY_DELAYS.length; i += 1) {
    const { data, error } = await client.auth.refreshSession().catch((e: unknown) => ({ data: { session: null }, error: e }))
    if (data.session?.access_token) return data.session.access_token
    // 저장된 세션 자체가 없으면(로그아웃) 더 해 볼 것이 없다
    if (!error || (error as { name?: string }).name === 'AuthSessionMissingError') return null
    const wait = RETRY_DELAYS[i]
    if (wait === undefined) break
    await new Promise((done) => setTimeout(done, wait))
  }
  return null
}

function refreshShared(): Promise<string | null> {
  refreshing ??= refreshOnce().finally(() => { refreshing = null })
  return refreshing
}

/** 지금 쓸 수 있는 토큰. 로그인하지 않았으면 null. `force` 면 늘 새로 받는다 (서버가 401 을 준 뒤). */
export async function accessToken(force = false): Promise<string | null> {
  try {
    if (force) return await refreshShared()
    const client = await loadSupabase()
    const { data, error } = await client.auth.getSession()
    const session = data.session
    if (session?.access_token) {
      const expiresAt = (session.expires_at ?? 0) * 1000
      if (expiresAt && expiresAt - Date.now() < EARLY_MS) return (await refreshShared()) ?? session.access_token
      return session.access_token
    }
    // 세션이 있었는데 새로 받기에 실패한 경우만 다시 해 본다 (처음부터 로그인 안 한 사람은 error 가 없다)
    return error ? await refreshShared() : null
  } catch {
    return null
  }
}

/** 토큰 헤더 (없으면 빈 값) */
export async function authHeaders(force = false): Promise<Record<string, string>> {
  const token = await accessToken(force)
  return token ? { authorization: `Bearer ${token}` } : {}
}

/**
 * 토큰 헤더 — 무엇이 실패해도 빈 값.
 * 로그인 없이도 쓰는 기능들이 이걸 **있으면 붙이는 값**으로 쓴다 — 붙으면 서버가 그 계정에 묶어 주고,
 * 없으면 기기 열쇠로만 돈다.
 */
export async function authHeadersOrEmpty(): Promise<Record<string, string>> {
  try {
    return await authHeaders()
  } catch {
    return {}
  }
}

/**
 * 토큰을 붙여 보내고, **401 이면 한 번 새로 받아 다시 보낸다.**
 * 끊기면 null (부르는 쪽의 지금 실패 처리를 그대로 탄다).
 */
export async function authFetch(input: string, init: RequestInit = {}): Promise<Response | null> {
  const send = (token: string | null): Promise<Response | null> => {
    const headers = new Headers(init.headers)
    if (token) headers.set('authorization', `Bearer ${token}`)
    return fetch(input, { ...init, headers }).catch(() => null)
  }
  const first = await send(await accessToken())
  if (first?.status !== 401) return first
  const token = await accessToken(true)
  if (!token) return first
  return send(token)
}
